import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { DEV_BYPASS_AUTH } from '../devFlags';

interface UserContextValue {
  /** B2C display name, e.g. "Amit Mishra". */
  name: string;
  /** B2C username, which is the work email address. */
  email: string;
  /** Two-letter avatar text derived from the display name. */
  initials: string;
}

const UserContext = createContext<UserContextValue>({
  name: '',
  email: '',
  initials: '',
});

/**
 * Exposes the signed-in identity from MSAL.
 *
 * The legacy app kept this in Redux (`LoginReducer.loggedInUserProfile`) fed by
 * a `/api/profile` call, but that call was only ever dispatched from the
 * username/password login path — which the UI never rendered — so the profile
 * was always null and the avatar always blank. Reading the account off MSAL
 * removes both the dead endpoint and the Redux store.
 */
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { accounts } = useMsal();

  const value = useMemo<UserContextValue>(() => {
    const account = accounts[0];
    // Under the dev bypass there is no B2C account, so label the session clearly
    // rather than rendering a blank avatar and empty profile.
    const name = account?.name ?? (DEV_BYPASS_AUTH ? 'Dev User' : '');
    const email = account?.username ?? (DEV_BYPASS_AUTH ? 'dev@localhost' : '');

    const parts = name.trim().split(/\s+/).filter(Boolean);
    const initials =
      parts.length > 1
        ? (parts[0][0] ?? '') + (parts[1][0] ?? '')
        : (parts[0]?.[0] ?? email[0] ?? '');

    return { name, email, initials: initials.toUpperCase() };
  }, [accounts]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
